import { useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useCouple } from "./useCouple";

export type BadgeCategory = "calendar" | "lists" | "todos";

const STORAGE_KEY = "wir_last_seen";
const COLLECTION_BY_CATEGORY: Record<BadgeCategory, string> = {
  calendar: "calendarEvents",
  lists: "lists",
  todos: "todos",
};

function loadLastSeen(): Partial<Record<BadgeCategory, number>> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");
  } catch {
    return {};
  }
}

// Called from each section's page on mount/unmount — marks "now" as seen so
// the bottom-nav dot clears for whatever was there when you arrived, without
// needing to compare against the exact latest item's timestamp.
export function markSeen(category: BadgeCategory) {
  const current = loadLastSeen();
  current[category] = Date.now();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
}

// A small, per-device "something's new here" signal — independent of push
// (which may be off, missed, or not yet granted) — so there's always a
// visual cue in the app itself, not just a notification that can be missed.
export function useUnseenBadges(): Partial<Record<BadgeCategory, boolean>> {
  const { couple } = useCouple();
  const coupleId = couple?.id ?? null;
  const [unseen, setUnseen] = useState<Partial<Record<BadgeCategory, boolean>>>({});

  useEffect(() => {
    if (!coupleId) {
      setUnseen({});
      return;
    }
    const lastSeen = loadLastSeen();
    const unsubs = (Object.keys(COLLECTION_BY_CATEGORY) as BadgeCategory[]).map((category) =>
      onSnapshot(
        query(collection(db, "couples", coupleId, COLLECTION_BY_CATEGORY[category]), orderBy("createdAt", "desc"), limit(1)),
        (snap) => {
          const ts = snap.docs[0]?.data().createdAt?.toMillis?.();
          if (!ts) return;
          setUnseen((prev) => ({ ...prev, [category]: ts > (lastSeen[category] ?? 0) }));
        }
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [coupleId]);

  return unseen;
}
